import {
	TimelineItemCategoryEnum,
	type TimelineItemResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { TimelineRowVariant } from "web_myplann/NotificationCenter/Types/TimelineRowVariant";
import { isContractNotificationItem } from "web_myplann/NotificationCenter/Utils/isContractNotificationItem";
import {
	parseTaskData,
	type TaskAuthor,
} from "web_myplann/NotificationCenter/Utils/parseTaskData";
import type { EventRowLines } from "web_myplann/Shared/Types/EventRowLines";
import { EventRowVariant } from "web_myplann/Shared/Types/EventRowVariant";
import { mapActivityRowLines } from "web_myplann/Shared/Utils/mapActivityRowLines";
import { mapTaskRowLines } from "web_myplann/Shared/Utils/mapTaskRowLines";
import { joinTruthy } from "web_myplann/Shared/Utils/textFormatters";
import { DOT_SEPARATOR } from "web_myplann/Utils/constants";

export const mapTimelineEventLines = (
	item: TimelineItemResponseDTO,
	dateLabel: string,
	taskLabel: string,
	rowVariant: TimelineRowVariant,
	formatAuthor: (author: TaskAuthor) => string,
): EventRowLines => {
	const { content } = item;

	if (item.category === TimelineItemCategoryEnum.Task && !item.isResolved) {
		const taskData = parseTaskData(item.data);

		if (taskData) {
			return mapTaskRowLines({
				variant: EventRowVariant.Preview,
				taskLabel,
				title: taskData.title,
				dueDate: dateLabel,
				person: taskData.author ? formatAuthor(taskData.author) : undefined,
			});
		}

		return {
			overline: dateLabel || undefined,
			title: content,
			variant: EventRowVariant.Preview,
			details: [],
		};
	}

	if (
		item.category === TimelineItemCategoryEnum.Task ||
		item.category === TimelineItemCategoryEnum.Activity
	) {
		if (content.length > 0) {
			return mapActivityRowLines({
				typeLabel: item.title,
				dateLabel,
				title: content,
				person: item.subContent ?? undefined,
			});
		}

		return {
			overline: dateLabel || undefined,
			title: item.title,
			variant: EventRowVariant.List,
			details: item.subContent ? [item.subContent] : [],
		};
	}

	const details = item.subContent ? [item.subContent] : [];

	if (rowVariant === TimelineRowVariant.Task) {
		if (item.category === TimelineItemCategoryEnum.Reminder) {
			return {
				...mapTaskRowLines({
					variant: EventRowVariant.Preview,
					taskLabel: item.title,
					title: content,
					dueDate: dateLabel,
				}),
				details,
			};
		}

		return {
			overline: dateLabel || undefined,
			title: content,
			variant: EventRowVariant.Preview,
			details,
		};
	}

	return {
		overline: joinTruthy([item.title, dateLabel], DOT_SEPARATOR),
		title: content || undefined,
		variant:
			item.category === TimelineItemCategoryEnum.Contract ||
			isContractNotificationItem(item)
				? EventRowVariant.Preview
				: EventRowVariant.List,
		details,
	};
};
